import PropTypes from 'prop-types'
import {Flex, Link, Radio, RadioGroup, Stack, Text} from '@chakra-ui/react'
import {Trans, useTranslation} from 'react-i18next'
import input from '~/react/theme/components/input'
import {useRails} from '~/react/contexts/rails'
import usePaymentMethods from '~/react/hooks/usePaymentMethods'
import BankLogo from '~/react/components/BankLogo'
import {SettingsCogIcon} from '~/react/icons'
import FormikFieldWrapper from '~/react/components/FormikFieldWrapper'
import {useField} from 'formik'
import {ExpandablePanel} from '../ExpandablePanel'
import {formPropTypes} from '../formPropTypes'
import {PayablePropType} from '../../../../propTypes'

const PaymentMethodDetails = ({paymentMethod}) => {
  const bankName = paymentMethod?.bankName
  const accountNumber = paymentMethod?.accountNumber
  const lastFour = accountNumber?.slice(-4)

  return (
    <Flex alignItems="center" gap="sm">
      <BankLogo providerName={bankName} />
      <Text textStyle="body-copy" textOverflow="ellipsis">
        {paymentMethod.accountName}
      </Text>
      {lastFour && (
        <>
          <Text whiteSpace="nowrap" fontSize="sm">
            ••••
          </Text>
          <Text whiteSpace="nowrap" fontSize="sm">
            {lastFour}
          </Text>
        </>
      )}
    </Flex>
  )
}

PaymentMethodDetails.propTypes = {
  paymentMethod: PropTypes.shape({
    accountName: PropTypes.string,
    bankName: PropTypes.string,
    accountNumber: PropTypes.string,
  }).isRequired,
}

const PaymentMethodForm = ({values, isValid}) => {
  const {t} = useTranslation('pointsOnPayments')
  const {currentSupplier} = useRails()

  const [field, _meta, {setValue, setTouched}] = useField({
    name: 'paymentMethodId',
    value: values.paymentMethodId,
  })

  const {groupedPaymentMethods} = usePaymentMethods(currentSupplier.id)

  const debitAgreements = groupedPaymentMethods.debitAgreement.map(
    (x) => x.debitAgreement,
  )

  const selectedPaymentMethod = debitAgreements.find(
    (method) => method.id === values.paymentMethodId,
  )

  const selectPaymentMethod = async (paymentMethodId) => {
    await setValue(paymentMethodId)
    await setTouched(true, {shouldValidate: true})
  }

  const handleSelectPaymentMethod = (paymentMethodId) => () =>
    selectPaymentMethod(paymentMethodId)

  return (
    <ExpandablePanel
      title={t(
        'pointsOnPayments:paymentWizard.formPanel.paymentMethodForm.title',
      )}
      isDisabled={!isValid}
      completedClosedContent={
        selectedPaymentMethod && (
          <PaymentMethodDetails paymentMethod={selectedPaymentMethod} />
        )
      }
    >
      <FormikFieldWrapper name="paymentMethodId" width="100%">
        <RadioGroup
          {...field}
          name="paymentMethodId"
          variant="mld"
          onChange={selectPaymentMethod}
        >
          <Stack gap="xs">
            {debitAgreements.map((paymentMethod) => (
              <Flex
                key={paymentMethod.id}
                {...input.variants.outline().field}
                justify="space-between"
                align="center"
                p="sm"
                cursor="pointer"
                onClick={handleSelectPaymentMethod(paymentMethod.id)}
              >
                <Radio value={paymentMethod.id} alignItems="center">
                  <PaymentMethodDetails paymentMethod={paymentMethod} />
                </Radio>
              </Flex>
            ))}
          </Stack>
        </RadioGroup>
      </FormikFieldWrapper>
      <Flex alignItems="center" bg="mld.neutral.50" p="sm" gap="sm">
        <SettingsCogIcon />
        <Text textStyle="body-small" color="mld.neutral.800">
          <Trans
            i18nKey="pointsOnPayments:paymentWizard.formPanel.paymentMethodForm.addMoreMessage"
            components={{
              linkToSettings: (
                <Link
                  variant="primary"
                  textStyle="body-small"
                  isExternal
                  href="/settings/payment_methods"
                />
              ),
            }}
          />
        </Text>
      </Flex>
    </ExpandablePanel>
  )
}

PaymentMethodForm.propTypes = {
  payable: PayablePropType,
  values: formPropTypes.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
}

PaymentMethodForm.defaultProps = {
  payable: null,
}

export default PaymentMethodForm
